import axios, { AxiosError } from 'axios';

/**
 * Chat Service
 *
 * Handles all chat-related API requests for the Requirements Assistant chatbot.
 */

// API Base URL from environment variable or default to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * Chat interfaces
 */
export type MessageType = 'user' | 'ai' | 'system';
export type ActionType = 'create_requirement' | 'update_requirement';
export type ActionStatus = 'pending' | 'approved' | 'rejected' | 'completed' | 'failed';

export interface ChatSession {
  id: string;
  project_id: string;
  user_id: string;
  company_id: string;
  session_name: string | null;
  total_tokens_used: number;
  message_count: number;
  created_at: string;
  last_activity_at: string;
  is_active: boolean;
}

export interface ChatMessage {
  id: string;
  session_id: string;
  user_id: string | null;
  message_type: MessageType;
  message_content: string;
  tokens_used: number | null;
  action_type: ActionType | null;
  action_status: ActionStatus | null;
  action_data: string | null;
  requirement_id: string | null;
  created_at: string;
  is_active: boolean;
}

export interface CreateSessionInput {
  session_name?: string;
}

export interface SendMessageInput {
  sessionId: string;
  message: string;
}

export interface ActionData {
  type: ActionType;
  requirementId?: string;
  data: {
    title?: string;
    description?: string;
    requirement_type?: string;
    priority?: string;
  };
  changes_explanation?: string;
}

export interface SendMessageResponse {
  messageId: string;
  aiResponse: string;
  tokensUsed: number;
  totalTokensUsed: number;
  tokenLimit: number;
  action: {
    type: ActionType;
    status: ActionStatus;
    data: ActionData;
  } | null;
}

export interface SessionWithMessages {
  session: ChatSession;
  messages: ChatMessage[];
}

export interface ApproveActionInput {
  messageId: string;
}

export interface RejectActionInput {
  messageId: string;
}

export interface TokenUsage {
  user: {
    daily: number;
    weekly: number;
    monthly: number;
    dailyLimit: number;
    weeklyLimit: number;
    monthlyLimit: number;
  };
  company: {
    daily: number;
    weekly: number;
    monthly: number;
    dailyLimit: number;
    weeklyLimit: number;
    monthlyLimit: number;
  };
}

export interface APIError {
  error: string;
  details?: string;
}

/**
 * Handle API errors
 */
function handleAPIError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<APIError>;
    const errorMessage = axiosError.response?.data?.error || axiosError.message;
    throw new Error(errorMessage);
  }
  throw error;
}

/**
 * Create a new chat session
 */
export async function createSession(
  projectId: string,
  input: CreateSessionInput,
  token: string
): Promise<ChatSession> {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/projects/${projectId}/chat/sessions`,
      input,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get all chat sessions for a project
 */
export async function getProjectSessions(
  projectId: string,
  token: string
): Promise<ChatSession[]> {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/projects/${projectId}/chat/sessions`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get a specific chat session with messages
 */
export async function getSession(
  projectId: string,
  sessionId: string,
  token: string
): Promise<SessionWithMessages> {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/projects/${projectId}/chat/sessions/${sessionId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Send a message and get AI response
 */
export async function sendMessage(
  projectId: string,
  input: SendMessageInput,
  token: string
): Promise<SendMessageResponse> {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/projects/${projectId}/chat/message`,
      input,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Approve an AI-proposed action
 */
export async function approveAction(
  projectId: string,
  input: ApproveActionInput,
  token: string
): Promise<any> {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/projects/${projectId}/chat/action/approve`,
      input,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Reject an AI-proposed action
 */
export async function rejectAction(
  projectId: string,
  input: RejectActionInput,
  token: string
): Promise<void> {
  try {
    await axios.post(
      `${API_BASE_URL}/projects/${projectId}/chat/action/reject`,
      input,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get token usage statistics
 */
export async function getUsage(
  projectId: string,
  token: string
): Promise<TokenUsage> {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/projects/${projectId}/chat/usage`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

export default {
  createSession,
  getProjectSessions,
  getSession,
  sendMessage,
  approveAction,
  rejectAction,
  getUsage
};
